/**
 * The one save engine of a section record. The section row has one version,
 * `updated_at`, so every tile edits through here: a typed fact saves when it is
 * left or Enter is pressed, and Escape puts back the saved value; a chosen fact
 * saves when it is chosen. Each save carries the version it was made against,
 * and the section it returns becomes the base for every tile, so a second save
 * never meets the first as a conflict.
 *
 * Saves and the lifecycle acts run one after another, so a click on Publish
 * that leaves a typed field waits for that field's save instead of racing it.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useTabDrafts, type TabDraftOwner } from "@/components/ui/box-draft";
import type { CourseFieldCommit } from "@/components/courses/CourseSectionFields";
import { ApiError } from "@/lib/api/client";
import { checkCommit, reasonOnError } from "@/lib/api/commitSeams";
import { sectionCommands } from "./section-commands";
import { currentOnConflict } from "@/lib/api/conflict";
import { coursesApi, type Course, type CreateCourse, type PatchCourse } from "@/lib/api/courses";
import {
  emptyCourseForm,
  courseFormFrom,
  courseFormToPayload,
  courseImageUrlProblem,
  type CourseForm,
  type SetCourseField,
} from "@/lib/course-form";
import { invalidateCourseDependentQueries } from "@/lib/course-query-keys";
import { readCourseSetupCopy, writeCourseSetupCopy } from "@/lib/course-setup-recovery";

type FormKey = keyof CourseForm;

const FIELD: Record<FormKey, keyof CreateCourse> = {
  code: "code",
  title: "title",
  credits: "credits",
  modality: "modality",
  programId: "program_id",
  semesterId: "semester_id",
  instructorFacultyProfileId: "primary_instructor_faculty_profile_id",
  meetingProvider: "meeting_provider",
  meetingWeekdays: "meeting_weekdays",
  meetingStartsAt: "meeting_starts_at",
  meetingEndsAt: "meeting_ends_at",
  timezone: "timezone",
  meetingUrl: "meeting_url",
  location: "location",
  capacity: "capacity",
  imageUrl: "image_url",
};
const FORM_KEYS = Object.keys(FIELD) as FormKey[];

/** Days, start, end and zone are one fact: they save together or not at all. */
const SCHEDULE_KEYS: readonly FormKey[] = ["meetingWeekdays", "meetingStartsAt", "meetingEndsAt", "timezone"];

/** A fact chosen from a list saves when it is chosen. */
const CHOSEN: ReadonlySet<FormKey> = new Set<FormKey>([
  "modality", "programId", "semesterId", "instructorFacultyProfileId", "meetingProvider",
  "meetingWeekdays", "meetingStartsAt", "meetingEndsAt", "timezone",
]);

export type SectionFact =
  | Exclude<FormKey, "meetingWeekdays" | "meetingStartsAt" | "meetingEndsAt" | "timezone">
  | "schedule";
export type SectionTileKey = "section" | "team" | "meets";

const TILE: Record<SectionFact, SectionTileKey> = {
  code: "section",
  title: "section",
  credits: "section",
  programId: "section",
  semesterId: "section",
  capacity: "section",
  imageUrl: "section",
  instructorFacultyProfileId: "team",
  modality: "meets",
  meetingProvider: "meets",
  meetingUrl: "meets",
  location: "meets",
  schedule: "meets",
};

export type SaveState =
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "failed"; reason: string | null; retry: boolean }
  | { kind: "conflict"; theirs: Course }
  | { kind: "unknown"; key: string };

interface FieldPending { id: string; key: string; fact: SectionFact; sent: CourseForm; keys: FormKey[] }

interface Status {
  fact: SectionFact;
  state: SaveState;
}

const factOf = (key: FormKey): SectionFact =>
  SCHEDULE_KEYS.includes(key) ? "schedule" : (key as SectionFact);
const keysOf = (fact: SectionFact): readonly FormKey[] => (fact === "schedule" ? SCHEDULE_KEYS : [fact]);
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

function withFact<K extends FormKey>(form: CourseForm, key: K, value: CourseForm[K]): CourseForm {
  return { ...form, [key]: value };
}

/** Why the schedule cannot be saved yet, or null when it is whole or empty. */
export function scheduleProblem(form: CourseForm): string | null {
  const days = form.meetingWeekdays.length > 0;
  const starts = form.meetingStartsAt.trim() !== "";
  const ends = form.meetingEndsAt.trim() !== "";
  if (days === starts && starts === ends) return null;
  return starts && ends ? "Choose meeting days" : "Add a start and end time";
}

/**
 * What the fields hold that the section does not, as the PATCH sends it. A
 * blank code or title, a link the server would refuse, and a partial schedule
 * stay in their fields and are not sent.
 */
function changesFrom(draft: CourseForm, base: Course): PatchCourse {
  const mine = courseFormToPayload(draft);
  const saved = courseFormToPayload(courseFormFrom(base));
  const held = new Set<FormKey>();
  if (!draft.code.trim()) held.add("code");
  if (!draft.title.trim()) held.add("title");
  if (courseImageUrlProblem(draft.imageUrl)) held.add("imageUrl");
  if (scheduleProblem(draft)) SCHEDULE_KEYS.forEach((key) => held.add(key));
  const differs = (key: FormKey) => !same(mine[FIELD[key]], saved[FIELD[key]]);
  const scheduleChanged = SCHEDULE_KEYS.some(differs);
  return Object.fromEntries(
    FORM_KEYS
      .filter((key) => !held.has(key) && (SCHEDULE_KEYS.includes(key) ? scheduleChanged : differs(key)))
      .map((key) => [FIELD[key], mine[FIELD[key]]]),
  ) as PatchCourse;
}

/** The facts typed here and not yet saved, sendable or not. */
function typedKeys(draft: CourseForm, base: Course): FormKey[] {
  const saved = courseFormFrom(base);
  return FORM_KEYS.filter((key) => !same(draft[key], saved[key]));
}

/** A field draft carries its original facts as well as the entry, so one
 * acknowledged field save can advance its base without ending another field's
 * unsent draft. Storage, scope and reload recovery belong to the kit. */
export function useSectionSave(course: Course | null, owner?: TabDraftOwner | null) {
  const queryClient = useQueryClient();
  const base = useRef(course);
  if (base.current?.id !== course?.id) base.current = course;
  // Ending a confirmed draft notifies the kit before the parent query's next
  // render. Its initial value must already be the confirmed record, otherwise
  // blur can send the previous value back as a new change.
  const latest = course && base.current && Date.parse(base.current.updated_at) > Date.parse(course.updated_at) ? base.current : course;
  const initial = latest ? courseFormFrom(latest) : emptyCourseForm("in_person");
  const drafts = useTabDrafts<{ form: CourseForm; base: CourseForm; legacyVersion?: string | null }, FieldPending>("course-details");
  const keep = drafts({
    record: course?.id ?? "unselected", version: course?.updated_at ?? null,
    initial: { form: initial, base: initial },
  });
  const keepNow = useRef(keep); keepNow.current = keep;
  const tenant = owner?.tenant ?? "", actor = owner?.user ?? "", record = course?.id ?? "";
  const legacy = useMemo(() => record ? readCourseSetupCopy(tenant, actor, record) : null, [tenant, actor, record]);
  const legacyAddress = `${tenant}/${actor}/${record}`;
  const [legacyResolved, setLegacyResolved] = useState<string | null>(null);
  const [legacyWarningFor, setLegacyWarningFor] = useState<string | null>(null);
  const legacyWarning = legacyWarningFor === legacyAddress;
  const [adoptingLegacy, setAdoptingLegacy] = useState<string | null>(null);
  const recovery = legacy && legacyResolved !== legacyAddress ? legacy : null;
  const recoveryNow = useRef(recovery); recoveryNow.current = recovery;
  // Remove the previous copy only after the kit proves it kept the adopted
  // value in this tab. A denied tab store must not erase the durable copy.
  useEffect(() => {
    if (adoptingLegacy !== legacyAddress || !legacy || !same(keep.value.form, legacy.form)) return;
    setLegacyWarningFor(!keep.inTab || !writeCourseSetupCopy(tenant, actor, record, null) ? legacyAddress : null);
    setAdoptingLegacy(null);
  }, [adoptingLegacy, legacyAddress, legacy, keep.value.form, keep.inTab, tenant, actor, record]);
  const sentNow = useRef<{ record: string | null; pending: FieldPending | null }>({ record: course?.id ?? null, pending: keep.pending });
  if (sentNow.current.record !== (course?.id ?? null)) sentNow.current = { record: course?.id ?? null, pending: keep.pending };
  const pendingFor = (id: string) => sentNow.current.record === id ? sentNow.current.pending : null;
  const clearPending = (id: string) => { if (sentNow.current.record === id) sentNow.current.pending = null; };
  const recordNow = useRef(course); recordNow.current = course;
  if (course && base.current && !keep.kept && Date.parse(course.updated_at) > Date.parse(base.current.updated_at)) base.current = course;
  const typed = useRef(keep.value.form); typed.current = keep.value.form;
  const [statuses, setStatuses] = useState<Record<string, Status>>({});
  const [busy, setBusy] = useState(false);
  const deciding = useRef<string | null>(null);
  const pending = useRef(0);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const statusKey = (id: string, fact: SectionFact) => `${id}:${fact}`;
  const report = (id: string, fact: SectionFact, state: SaveState | null) => setStatuses(was => {
    const next = { ...was }; const key = statusKey(id, fact);
    if (state) next[key] = { fact, state }; else delete next[key];
    return next;
  });
  const currentBase = (): Course => {
    if (!base.current || base.current.id !== recordNow.current?.id) throw new Error("Read this section before changing it.");
    return base.current;
  };
  const dirty = (form: CourseForm, from: CourseForm) => FORM_KEYS.filter(key => !same(form[key], from[key]));
  const legacyConflict = keep.value.legacyVersion !== undefined && keep.value.legacyVersion !== course?.updated_at;
  const conflict = keep.conflict !== null || legacyConflict || Boolean(course && Object.values(statuses).some(item => item.state.kind === "conflict" && statuses[statusKey(course.id, item.fact)] === item));
  const hasConflict = () => keepNow.current.conflict !== null || deciding.current === recordNow.current?.id || keepNow.current.value.legacyVersion !== undefined && keepNow.current.value.legacyVersion !== recordNow.current?.updated_at;
  const edit = (next: CourseForm) => {
    typed.current = next;
    keepNow.current.set(was => ({ ...was, form: next }));
  };
  const publish = (saved: Course) => {
    queryClient.setQueryData(["course", saved.id], saved);
    invalidateCourseDependentQueries(queryClient);
  };
  const adopt = (saved: Course, sent: CourseForm, sentKeys: ReadonlySet<FormKey>, confirmed?: (version?: string) => void) => {
    // A return for another visited record never changes the new record's entry.
    // Its old tab draft remains for an explicit version comparison when reopened.
    if (recordNow.current?.id !== saved.id) { publish(saved); return; }
    const before = courseFormFrom(currentBase()); const after = courseFormFrom(saved);
    let next = typed.current;
    for (const key of FORM_KEYS) {
      if (same(typed.current[key], sent[key]) && (sentKeys.has(key) || same(sent[key], before[key]))) next = withFact(next, key, after[key]);
    }
    base.current = saved; typed.current = next;
    if (dirty(next, after).length) {
      keepNow.current.set({ form: next, base: after });
      confirmed?.(saved.updated_at);
    } else keepNow.current.end();
    publish(saved);
  };
  const enqueue = <T,>(job: () => Promise<T>): Promise<T> => {
    pending.current++; setBusy(true);
    const run = queue.current.then(job).finally(() => { if (--pending.current === 0) setBusy(false); });
    queue.current = run.catch(() => undefined); return run;
  };
  const send = async (fact: SectionFact, id: string) => {
    if (recordNow.current?.id !== id || hasConflict() || recoveryNow.current || !keepNow.current.settled || pendingFor(id)) return;
    const current = currentBase();
    const allChanges = changesFrom(typed.current, current);
    const changes = Object.fromEntries(Object.entries(allChanges).filter(([key]) => keysOf(fact).some(own => FIELD[own] === key))) as PatchCourse;
    const sentKeys = new Set(FORM_KEYS.filter(key => FIELD[key] in changes));
    if (!sentKeys.size) return;
    const sent = typed.current;
    const held = keepNow.current;
    const key = crypto.randomUUID();
    const pending = { id, key, fact, sent, keys: [...sentKeys] };
    sentNow.current = { record: id, pending };
    held.setPending(pending);
    const confirmed = held.sending();
    report(id, fact, { kind: "saving" });
    try {
      const saved = await sectionCommands.save(id, { ...changes, expected_updated_at: current.updated_at }, key);
      held.setPending(null); clearPending(id);
      adopt(saved, sent, sentKeys, confirmed); report(id, fact, { kind: "saved" });
    } catch (error) {
      const theirs = currentOnConflict<Course>(error);
      if (theirs) { held.setPending(null); clearPending(id); deciding.current = id; publish(theirs); report(id, fact, { kind: "conflict", theirs }); }
      else if (error instanceof ApiError && error.status >= 400 && error.status < 500 && ![408, 429].includes(error.status)) {
        held.setPending(null); clearPending(id);
        report(id, fact, { kind: "failed", reason: reasonOnError(error)?.message ?? error.message, retry: false });
      } else report(id, fact, { kind: "unknown", key });
    }
  };
  const checkFact = async () => {
    const held = keepNow.current;
    const sent = recordNow.current ? pendingFor(recordNow.current.id) : null;
    if (!sent) return;
    try {
      const checked = await checkCommit<{ section_id: string; version: string }>(sent.key, value => {
        if (!value || typeof value !== "object" || !("section_id" in value) || !("version" in value) || typeof value.section_id !== "string" || typeof value.version !== "string") throw new TypeError("The saved section receipt could not be read.");
        return value as { section_id: string; version: string };
      });
      if (checked.state === "not_committed") {
        held.setPending(null); clearPending(sent.id);
        report(sent.id, sent.fact, { kind: "failed", reason: "The server has no committed save for this request. The field is kept.", retry: true });
        return;
      }
      if (checked.state !== "confirmed" || checked.receipt.action !== "course.patch" || !checked.receipt.subject_ids.includes(sent.id) || checked.receipt.result.section_id !== sent.id) return;
      const saved = await coursesApi.get(sent.id);
      held.setPending(null); clearPending(sent.id);
      if (saved.updated_at !== checked.receipt.result.version) {
        if (recordNow.current?.id === sent.id) deciding.current = sent.id;
        publish(saved); report(sent.id, sent.fact, { kind: "conflict", theirs: saved });
        return;
      }
      const confirmed = held.sending(true);
      adopt(saved, sent.sent, new Set(sent.keys), confirmed);
      report(sent.id, sent.fact, { kind: "saved" });
    } catch { report(sent.id, sent.fact, { kind: "unknown", key: sent.key }); }
  };
  const save = (fact: SectionFact) => { if (course) void enqueue(() => send(fact, course.id)); };
  const acknowledged = () => {
    if (!keepNow.current.settled) throw new Error("Wait for this tab's draft before continuing.");
    if (recoveryNow.current) throw new Error("Choose what to keep from the previous editor before continuing.");
    if (recordNow.current && pendingFor(recordNow.current.id)) throw new Error("Check the field already sent before continuing.");
    if (hasConflict()) throw new Error("Resolve the newer section version before continuing.");
    const current = currentBase();
    if (typedKeys(typed.current, current).length) throw new Error("Save or correct every unfinished fact before continuing.");
    return current;
  };
  const review = (): Promise<Course> => enqueue(async () => acknowledged());
  const act = (write: (current: Course) => Promise<Course>, expectedVersion?: string): Promise<Course> => enqueue(async () => {
    const current = acknowledged();
    if (expectedVersion && current.updated_at !== expectedVersion) throw new Error("The section changed after this review. Review its saved facts again.");
    const sent = typed.current;
    try { const saved = await write(current); adopt(saved, sent, new Set()); return saved; }
    catch (error) {
      const theirs = currentOnConflict<Course>(error);
      if (theirs) { deciding.current = current.id; publish(theirs); report(current.id, "title", { kind: "conflict", theirs }); }
      throw error;
    }
  });
  const setField: SetCourseField = (key, value) => {
    if (!course || !keepNow.current.settled || recoveryNow.current) return;
    edit(withFact(typed.current, key, value)); report(course.id, factOf(key), null);
    if (CHOSEN.has(key)) save(factOf(key));
  };
  const restore = (key: FormKey) => {
    if (!course) return;
    edit(withFact(typed.current, key, courseFormFrom(currentBase())[key])); report(course.id, factOf(key), null);
  };
  const commit = (key: FormKey): CourseFieldCommit => ({
    onBlur: () => save(factOf(key)),
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") { event.preventDefault(); save(factOf(key)); }
      else if (event.key === "Escape") { event.preventDefault(); restore(key); }
    },
  });
  const keepMine = () => {
    if (!course) return;
    const newer = courseFormFrom(course);
    let merged = newer;
    for (const key of dirty(typed.current, keepNow.current.value.base)) merged = withFact(merged, key, typed.current[key]);
    base.current = course; deciding.current = null; typed.current = merged;
    keepNow.current.set({ form: merged, base: newer });
    keepNow.current.keepMine();
    setStatuses(was => Object.fromEntries(Object.entries(was).filter(([key]) => !key.startsWith(`${course.id}:`))));
  };
  const loadTheirs = () => {
    if (!course) return;
    base.current = course; deciding.current = null; typed.current = courseFormFrom(course); keepNow.current.loadNewer();
    setStatuses(was => Object.fromEntries(Object.entries(was).filter(([key]) => !key.startsWith(`${course.id}:`))));
  };
  const stateOf = (fact: SectionFact): SaveState | null => {
    if (!course) return null;
    const own = statuses[statusKey(course.id, fact)]?.state ?? null;
    if (keep.pending?.fact === fact && own?.kind !== "saving") return { kind: "unknown", key: keep.pending.key };
    if (own?.kind === "conflict") return own;
    return (keep.conflict || legacyConflict) && keysOf(fact).some(key => !same(keep.value.form[key], keep.value.base[key])) ? { kind: "conflict", theirs: course } : own;
  };
  const shownIn = (tile: SectionTileKey) => (Object.keys(TILE) as SectionFact[]).map(fact => ({ fact, state: stateOf(fact) })).find(item => TILE[item.fact] === tile && item.state);
  return {
    draft: keep.value.form, busy, hasUnsaved: !!recovery || dirty(keep.value.form, keep.value.base).length > 0 || conflict || keep.pending !== null,
    settled: keep.settled, kept: keep.kept, inTab: keep.inTab,
    recovery, legacyTransferWarning: legacyWarning, recoveryUnavailable: legacyWarning || keep.kept && !keep.inTab,
    resumeRecovery: () => {
      if (!course || !recovery || keepNow.current.pending) return;
      keepNow.current.set({ form: recovery.form, base: recovery.base, legacyVersion: recovery.version });
      typed.current = recovery.form;
      setLegacyResolved(legacyAddress); setAdoptingLegacy(legacyAddress);
    },
    discardRecovery: () => {
      if (!recovery) return;
      setLegacyWarningFor(!writeCourseSetupCopy(tenant, actor, record, null) ? legacyAddress : null);
      setLegacyResolved(legacyAddress);
    },
    recoveryCanResume: !keep.pending,
    review, setField, commit, act, keepMine, loadTheirs,
    saveUnfinished: () => { if (course) for (const fact of new Set(typedKeys(typed.current, currentBase()).map(factOf))) save(fact); },
    retry: () => { if (course) for (const fact of Object.keys(TILE) as SectionFact[]) if (stateOf(fact)?.kind === "failed") save(fact); },
    checkFact, retryFact: save, removeImage: () => save("imageUrl"),
    stateOf, stateIn: (tile: SectionTileKey): SaveState | null => shownIn(tile)?.state ?? null,
    failureOf: (fact: SectionFact): string | null => { const state = stateOf(fact); return state?.kind === "failed" ? state.reason : null; },
  };
}

export type SectionSave = ReturnType<typeof useSectionSave>;
